import { useState, useEffect, useMemo } from 'react';
import { loadEquipmentFromProxy } from './api';
import { equipmentState } from './state';
import * as selectors from './selectors';

/**
 * React Hook для использования данных оборудования.
 */
export function useEquipmentStore() {
    const cached = equipmentState.getData();
    const [equipment, setEquipment] = useState(() => cached || []);
    const [loading, setLoading] = useState(!cached);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (equipmentState.getData()) {
            setEquipment(equipmentState.getData());
            setLoading(false);
            return;
        }

        let isMounted = true;
        loadEquipmentFromProxy()
            .then(data => {
                if (isMounted) {
                    setEquipment(data);
                    setLoading(false);
                }
            })
            .catch(err => {
                if (isMounted) {
                    setError(err);
                    setLoading(false);
                }
            });

        return () => { isMounted = false; };
    }, []);

    // Используем useMemo для эффективного возврата геттеров
    return useMemo(() => ({
        equipment,
        loading,
        error,
        isDataLoaded: !!equipment.length && !loading,

        // Проксируем геттеры напрямую из селекторов, привязывая к текущему состоянию оборудования
        getPumps: () => selectors.getPumps(equipment),
        getFilters: () => selectors.getFilters(equipment),
        getHeatExchangers: () => selectors.getHeatExchangers(equipment),
        getElectricHeaters: () => selectors.getElectricHeaters(equipment),
        getHeatPumps: () => selectors.getHeatPumps(equipment),
        getSkimmers: () => selectors.getSkimmers(equipment),
        getAutofill: () => selectors.getAutofill(equipment),
        getSkimmerCalculationCatalog: () => selectors.getSkimmerCalculationCatalog(equipment),
        getNozzles: () => selectors.getNozzles(equipment),
        getDrains: () => selectors.getDrains(equipment),
        getGrates: () => selectors.getGrates(equipment),
        getGrateProfile: () => selectors.getGrateProfile(equipment),
        getGrateCorners: () => selectors.getGrateCorners(equipment),
        getGrateOverflowFunnel: () => selectors.getGrateOverflowFunnel(equipment),
        getGrateCalculationCatalog: () => selectors.getGrateCalculationCatalog(equipment),
        getChlorinators: () => selectors.getChlorinators(equipment),
        getUltraviolet: () => selectors.getUltraviolet(equipment),
        getPVC: () => selectors.getPVC(equipment),
        getMosaic: () => selectors.getMosaic(equipment),
        getProjectors: () => selectors.getProjectors(equipment),
        getTransformers: () => selectors.getTransformers(equipment),
        getLadders: () => selectors.getLadders(equipment),
        getSand: () => selectors.getSand(equipment),
        getConsoles: () => selectors.getConsoles(equipment),
        getProjectorEmbedded: () => selectors.getProjectorEmbedded(equipment),
        getMonoblocks: () => selectors.getMonoblocks(equipment),
    }), [equipment, loading, error]);
}
